import React from 'react';
import { CalendarDays, MapPin, Shield } from 'lucide-react';
import PublicTimeline from './PublicTimeline';
import PublicResultActions from './PublicResultActions';
import { useTenant } from '../../contexts/TenantContext';
import { receiptBrandFromTenant } from '../../utils/tenantBranding';
import {
  COMPLAINT_STATUS,
  complaintTimeline,
  daysSince,
  formatPublicDate,
  statusMeta,
  toneClass,
} from '../../utils/publicStatus';

const PublicDenunciaResult = ({ row, orgName }) => {
  const { tenant } = useTenant();
  const st = statusMeta(COMPLAINT_STATUS, row.status, row.status_label);
  const when = formatPublicDate(row.data);
  const elapsed = daysSince(row.data);
  const timeline = complaintTimeline(row.status);
  const protocol = row.protocolo || row.id;

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-5 py-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Protocolo anônimo</p>
          <p className="mt-0.5 font-mono text-lg font-bold tracking-tight text-slate-900">{protocol}</p>
        </div>
        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${toneClass(st.tone)}`}>
          {st.label}
        </span>
      </div>

      <div className="space-y-5 px-5 py-5">
        <dl className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl bg-slate-50 px-3 py-3">
            <dt className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <CalendarDays className="h-3.5 w-3.5" aria-hidden />
              Registro
            </dt>
            <dd className="mt-1 text-sm font-semibold text-slate-900">{when || 'Data não publicada'}</dd>
            {elapsed != null ? (
              <dd className="mt-0.5 text-xs text-slate-500">
                {elapsed === 0 ? 'Registrada hoje' : `Há ${elapsed} dia${elapsed === 1 ? '' : 's'}`}
              </dd>
            ) : null}
          </div>
          <div className="rounded-xl bg-slate-50 px-3 py-3">
            <dt className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
              <MapPin className="h-3.5 w-3.5" aria-hidden />
              Município informado
            </dt>
            <dd className="mt-1 text-sm font-semibold text-slate-900">{row.cidade || 'Não informado'}</dd>
          </div>
        </dl>

        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Andamento público</p>
          <PublicTimeline steps={timeline.steps} current={timeline.current} />
        </div>

        <div className="rounded-xl bg-emerald-50/80 px-4 py-3 text-sm text-emerald-950 ring-1 ring-emerald-100">
          <p className="flex items-center gap-1.5 font-semibold">
            <Shield className="h-4 w-4" aria-hidden />
            Situação para o cidadão
          </p>
          <p className="mt-1 leading-relaxed">{st.hint}</p>
          <p className="mt-2 text-xs text-emerald-900/80">
            A consulta pública não mostra o texto da denúncia nem dados de quem relatou — só o andamento.
          </p>
        </div>

        <PublicResultActions
          protocol={protocol}
          sharePath="/public-complaint"
          shareParam="protocolo"
          receipt={{
            ...receiptBrandFromTenant(tenant, orgName),
            title: 'Consulta pública de denúncia anônima',
            protocol,
            fields: [
              ['Situação', st.label],
              ['Município', row.cidade || 'Não informado'],
              ['Registro', when],
            ],
            note: `${st.hint} Este comprovante não identifica o denunciante.`,
          }}
        />
      </div>
    </article>
  );
};

export default PublicDenunciaResult;
